// NEW standalone compact upload area (with download) for the redesigned ("v2")
// Track Record UI. It is a copy of @roc/ui's FileUploadAreaWithDownload rendered
// in a compact horizontal layout. Kept here instead of adding a `compact` prop
// to the shared component so @roc/ui stays untouched.
//
// FileUpload / FileUploadItemWithDownload are not re-exported from the @roc/ui
// barrel, so they are imported directly from their source module.

import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme, alpha } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useDropzone } from 'react-dropzone';
import { FileUpload, FileUploadItemWithDownload } from 'libs/ui/src/lib/components/fileUploadModalWithDownload/fileUploadModalWithDownload';
import { useState } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  compactDragArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    width: '100%',
    minHeight: '110px',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    padding: '20px 24px',
    marginBottom: 10,
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 8,
    gap: 16,
  },
  compactErrorDragArea: {
    border: `2px dashed ${theme.palette.error.main}`,
    width: '100%',
    minHeight: '110px',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    padding: '20px 24px',
    marginBottom: 10,
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 8,
    gap: 16,
  },
  compactIcon: {
    width: 40,
    height: 40,
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  compactText: {
    color: theme.palette.primary.main,
    fontSize: 14,
    lineHeight: 1.4,
    margin: 0,
  },
  compactLink: {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
    fontWeight: 600,
  },
  errorMeasage: {
    color: theme.palette.error.main,
  },
}));

const UploadDocumentIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M12 18V12M12 12L9 15M12 12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface ICompactFileUploadAreaWithDownloadProps {
  items: FileUpload[];
  setItems: (items) => void;
  multiple?: boolean;
  fileType?: string;
  fileTypes?: string[];
  showError?: boolean;
  maxFiles?: number;
  store: any;
  // Noun shown in the prompt: "Drag & Drop <documentLabel> here ...". Lets each
  // upload area describe what it expects (e.g. "your GC license").
  documentLabel?: string;
  // Optional override for downloading an uploaded row (routes around store.downloadTaskFile).
  downloadHandler?: (taskId: any, name: string, fileExtension?: string) => void;
}

export const CompactFileUploadAreaWithDownload = observer(
  ({ items, setItems, multiple, fileType, showError, fileTypes, maxFiles, store, documentLabel = 'your document', downloadHandler }: ICompactFileUploadAreaWithDownloadProps) => {
    const classes = useStyles();
    const [error, setError] = useState<boolean>(false);
    // Shim downloadTaskFile so the shared @roc/ui item downloads via downloadHandler.
    const itemStore = downloadHandler ? { downloadTaskFile: downloadHandler } : store;
    const isCorrectFileType = (files: any[]) => {
      let isValid = true;
      if (fileType) {
        files.forEach(file => {
          if (file.type?.toLowerCase() !== fileType.toLowerCase()) {
            isValid = false;
            return;
          }
        })
      }

      if (fileTypes) {
        files.forEach(file => {
          const dotSplitFileName = file.name.split('.');
          if (!fileTypes.includes(dotSplitFileName[dotSplitFileName.length - 1])) {
            isValid = false;
            return;
          }
        });
      }

      return isValid;
    }

    const onDrop = (acceptedFiles: File[]) => {
      const newItems = acceptedFiles.map(file => ({
        name: file.name,
        file: file,
        type: file.name.split('.')[1]
      }));
      if (isCorrectFileType(newItems)) {
        setItems(prevItems => [...(multiple ? prevItems : []), ...newItems]);
        setError(false);
      } else {
        setError(true);
      }
    };

    const onRemove = (index: number) => {
      setItems(prevFiles => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);
        return newFiles;
      });
    };

    const onRename = (index: number, newName: string) => {
      setItems(prevItems => {
        const items = [...prevItems];
        items[index].name = newName;
        return items;
      });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple,
      maxFiles: multiple ? maxFiles ? maxFiles : 0 : 1,
    });

    const showDropArea = (multiple && !maxFiles) || (multiple && maxFiles && items.length < maxFiles) || (!multiple && items.length === 0);
    const hasError = error || showError;

    return (
      <>
        {showDropArea &&
          <Box {...getRootProps()} className={hasError ? classes.compactErrorDragArea : classes.compactDragArea}>
            <input {...getInputProps()} />
            <UploadDocumentIcon className={classes.compactIcon} />
            {isDragActive ? (
              <p className={classes.compactText}>Drop the file(s) here...</p>
            ) : (
              <p className={classes.compactText}>
                Drag &amp; Drop {documentLabel} here or <span className={classes.compactLink}>click here to upload</span>
              </p>
            )}
          </Box>
        }
        {error && <Typography variant="caption" className={classes.errorMeasage}>
          * The file you are trying to upload does not have the expected file type
        </Typography>}
        <Box>
          {items.map((item, i) => {
            // Skip blank rows: an item with no usable name and no file would render
            const label = item?.name || item?.originalFileName || (item?.file && item.file.name);
            if (!label) return null;
            return (
              <FileUploadItemWithDownload
                key={item.file?.name ?? item.name ?? i}
                name={item.name ?? item.originalFileName ?? ''}
                file={item.file}
                taskId={item.taskId}
                originalFileName={item.originalFileName}
                fileExtension={item.fileExtension}
                disabled={item.disabled}
                onSaveName={newName => onRename(i, newName)}
                onRemove={() => onRemove(i)}
                store={itemStore}
              />
            );
          })}
        </Box>
      </>
    );
  }
);

export default CompactFileUploadAreaWithDownload;
